import { useEffect, useRef } from 'react'

// Everything that floats above a screen — sheets, wizards, record documents,
// the command palette, the anchored menu — goes through here, so that the
// behaviours you only notice when they're missing are written once: Escape
// closes the topmost thing and only the topmost thing, focus moves into the
// panel and comes back to whatever opened it, and Tab can't wander out onto
// the page behind.
//
// Opening any overlay closes every other one. That discipline lives with the
// callers (one open(patch) that resets the rest, per 00-foundation.md §5.2) —
// what this module guarantees is that if two ever *are* open at once, Escape
// still dismisses them one at a time, newest first.

/**
 * Overlays are positioned against the app frame rather than the viewport, so
 * they portal into it. That is what makes `position:absolute; inset:34px` on a
 * record document mean what the prototype means by it: the frame is fixed to
 * the viewport and the body scrolls inside it, so there is no scroll offset for
 * an overlay to have to know about.
 *
 * Falls back to <body> for the marketing and auth pages, which have no frame —
 * there the app scrolls normally and a fixed overlay is the right answer.
 */
export function overlayRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  return document.querySelector<HTMLElement>('.app-frame') ?? document.body
}

const stack: Array<() => void> = []

function isTopmost(close: () => void) {
  return stack[stack.length - 1] === close
}

const FOCUSABLE =
  'a[href],area[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),[tabindex]:not([tabindex="-1"])'

function focusableIn(panel: HTMLElement) {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  )
}

/**
 * Wire one overlay panel up: Escape, the focus trap, and focus return.
 *
 * `onClose` is read through a ref rather than depended on, so a caller passing
 * an inline arrow doesn't tear the listeners down and rebuild them — and, more
 * to the point, doesn't push itself onto the stack twice.
 */
export function useOverlay(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return

    const dismiss = () => close.current()
    stack.push(dismiss)

    // Whatever had focus when this opened is where focus goes back to. Captured
    // here rather than on mount of the trigger, because the trigger is often a
    // row that the overlay itself is about to re-render.
    const opener = document.activeElement as HTMLElement | null

    // The panel takes focus rather than its first control: a sheet that opens
    // with the cursor already in a text field reads as having been typed into.
    // The exception is a sheet whose whole purpose is one field, which sets
    // autofocus itself and wins, because this runs first.
    panel.current?.focus({ preventScroll: true })

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (!isTopmost(dismiss)) return
        event.stopPropagation()
        dismiss()
        return
      }

      if (event.key !== 'Tab') return
      const el = panel.current
      if (!el || !isTopmost(dismiss)) return

      const items = focusableIn(el)
      if (items.length === 0) {
        // Nothing to move between, so Tab has nowhere to go but out. Keep it in.
        event.preventDefault()
        return
      }

      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === el)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const at = stack.indexOf(dismiss)
      if (at !== -1) stack.splice(at, 1)
      // Only if focus is still somewhere inside what's closing — if the user
      // has already clicked elsewhere, dragging them back is the rude answer.
      if (opener?.isConnected && panel.current?.contains(document.activeElement)) {
        opener.focus({ preventScroll: true })
      }
    }
  }, [open])

  return panel
}
